/**
 * MovesetPanel - Shared Component
 * 
 * Displays Fast Move and Charged Moves in a visually prominent panel.
 * Used in PvP rankings and Pokémon detail pages.
 * 
 * Design System: Information Priority - Actionable (Hero Section)
 */

import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translateMoveName, useGameI18nReady } from '../../utils/gameI18n';

// =====================================================
// Comprehensive Move → Element Type Mapping
// Based on Pokemon GO game data
// =====================================================
const MOVE_TYPE_MAP = {
  // ===== NORMAL =====
  'tackle': 'Normal', 'scratch': 'Normal', 'pound': 'Normal', 'cut': 'Normal',
  'quick attack': 'Normal', 'hyper beam': 'Normal', 'body slam': 'Normal',
  'return': 'Normal', 'frustration': 'Normal', 'present': 'Normal',
  'swift': 'Normal', 'struggle': 'Normal', 'skull bash': 'Normal',
  'hyper fang': 'Normal', 'stomp': 'Normal', 'wrap': 'Normal',
  'take down': 'Normal', 'giga impact': 'Normal', 'last resort': 'Normal',
  'hidden power': 'Normal', 'yawn': 'Normal', 'locked on': 'Normal',
  'lock on': 'Normal', 'lock-on': 'Normal', 'boomburst': 'Normal',
  'tera blast normal': 'Normal', 'tri attack': 'Normal', 'techno blast normal': 'Normal',
  'weather ball normal': 'Normal', 'weather ball': 'Normal',
  
  // ===== FIRE =====
  'ember': 'Fire', 'fire spin': 'Fire', 'fire fang': 'Fire', 'incinerate': 'Fire',
  'fire punch': 'Fire', 'fire blast': 'Fire', 'flamethrower': 'Fire',
  'flame charge': 'Fire', 'flame wheel': 'Fire', 'overheat': 'Fire',
  'blast burn': 'Fire', 'heat wave': 'Fire', 'sacred fire': 'Fire',
  'blaze kick': 'Fire', 'v-create': 'Fire', 'magma storm': 'Fire',
  'weather ball fire': 'Fire', 'techno blast burn': 'Fire',
  'fusion flare': 'Fire', 'tera blast fire': 'Fire', 'fire weather ball': 'Fire',
  'mystical fire': 'Fire', 'burning jealousy': 'Fire', 'bitter malice': 'Fire',
  
  // ===== WATER =====
  'water gun': 'Water', 'bubble': 'Water', 'waterfall': 'Water', 'splash': 'Water',
  'aqua tail': 'Water', 'hydro pump': 'Water', 'surf': 'Water',
  'water pulse': 'Water', 'aqua jet': 'Water', 'brine': 'Water',
  'scald': 'Water', 'hydro cannon': 'Water', 'origin pulse': 'Water',
  'crabhammer': 'Water', 'razor shell': 'Water', 'octazooka': 'Water',
  'liquidation': 'Water', 'weather ball water': 'Water', 'techno blast chill': 'Water',
  'water shuriken': 'Water', 'tera blast water': 'Water', 'whirlpool': 'Water',
  'dive': 'Water',
  
  // ===== GRASS =====
  'vine whip': 'Grass', 'razor leaf': 'Grass', 'bullet seed': 'Grass',
  'magical leaf': 'Grass', 'seed bomb': 'Grass', 'solar beam': 'Grass',
  'solar blade': 'Grass', 'power whip': 'Grass', 'petal blizzard': 'Grass',
  'leaf blade': 'Grass', 'grass knot': 'Grass', 'frenzy plant': 'Grass',
  'energy ball': 'Grass', 'leaf tornado': 'Grass', 'leaf storm': 'Grass',
  'tera blast grass': 'Grass', 'trailblaze': 'Grass',
  
  // ===== ELECTRIC =====
  'thunder shock': 'Electric', 'spark': 'Electric', 'charge beam': 'Electric',
  'volt switch': 'Electric', 'thunder fang': 'Electric',
  'thunderbolt': 'Electric', 'thunder': 'Electric', 'discharge': 'Electric',
  'thunder punch': 'Electric', 'wild charge': 'Electric', 'zap cannon': 'Electric',
  'fusion bolt': 'Electric', 'volt tackle': 'Electric',
  'tera blast electric': 'Electric', 'parabolic charge': 'Electric',
  'nuzzle': 'Electric', 'electro ball': 'Electric',
  
  // ===== ICE =====
  'ice shard': 'Ice', 'frost breath': 'Ice', 'ice fang': 'Ice', 'powder snow': 'Ice',
  'ice beam': 'Ice', 'blizzard': 'Ice', 'ice punch': 'Ice',
  'icy wind': 'Ice', 'avalanche': 'Ice', 'aurora beam': 'Ice',
  'glaciate': 'Ice', 'weather ball ice': 'Ice', 'techno blast frost': 'Ice',
  'triple axel': 'Ice', 'tera blast ice': 'Ice',
  'ice weather ball': 'Ice',
  
  // ===== FIGHTING =====
  'counter': 'Fighting', 'karate chop': 'Fighting', 'low kick': 'Fighting',
  'rock smash': 'Fighting', 'force palm': 'Fighting',
  'dynamic punch': 'Fighting', 'close combat': 'Fighting',
  'cross chop': 'Fighting', 'focus blast': 'Fighting',
  'low sweep': 'Fighting', 'submission': 'Fighting', 'brick break': 'Fighting',
  'superpower': 'Fighting', 'aura sphere': 'Fighting', 'sacred sword': 'Fighting',
  'power-up punch': 'Fighting', 'power up punch': 'Fighting',
  'flying press': 'Fighting', 'drain punch': 'Fighting',
  'double kick': 'Fighting', 'tera blast fighting': 'Fighting',
  
  // ===== POISON =====
  'poison jab': 'Poison', 'poison sting': 'Poison', 'acid': 'Poison',
  'sludge bomb': 'Poison', 'sludge wave': 'Poison', 'gunk shot': 'Poison',
  'sludge': 'Poison', 'poison fang': 'Poison', 'cross poison': 'Poison',
  'acid spray': 'Poison', 'tera blast poison': 'Poison',
  
  // ===== GROUND =====
  'mud shot': 'Ground', 'mud slap': 'Ground', 'sand attack': 'Ground',
  'earthquake': 'Ground', 'dig': 'Ground', 'bone club': 'Ground',
  'mud bomb': 'Ground', 'bulldoze': 'Ground', 'drill run': 'Ground',
  'earth power': 'Ground', 'sand tomb': 'Ground', 'precipice blades': 'Ground',
  'high horsepower': 'Ground', 'tera blast ground': 'Ground',
  'scorching sands': 'Ground', 'stomping tantrum': 'Ground',
  
  // ===== FLYING =====
  'air slash': 'Flying', 'wing attack': 'Flying', 'peck': 'Flying',
  'gust': 'Flying', 'fairy wind': 'Flying',
  'aerial ace': 'Flying', 'air cutter': 'Flying', 'brave bird': 'Flying',
  'drill peck': 'Flying', 'hurricane': 'Flying', 'sky attack': 'Flying',
  'fly': 'Flying', 'aeroblast': 'Flying', 'feather dance': 'Flying',
  'acrobatics': 'Flying', 'oblivion wing': 'Flying',
  'tera blast flying': 'Flying',
  
  // ===== PSYCHIC =====
  'confusion': 'Psychic', 'psycho cut': 'Psychic', 'zen headbutt': 'Psychic',
  'extrasensory': 'Psychic',
  'psychic': 'Psychic', 'psystrike': 'Psychic', 'psybeam': 'Psychic',
  'future sight': 'Psychic', 'mirror coat': 'Psychic',
  'synchronoise': 'Psychic', 'heart stamp': 'Psychic',
  'psycho boost': 'Psychic', 'tera blast psychic': 'Psychic',
  'psyshock': 'Psychic', 'expanding force': 'Psychic',
  
  // ===== BUG =====
  'bug bite': 'Bug', 'fury cutter': 'Bug', 'struggle bug': 'Bug',
  'infestation': 'Bug',
  'x-scissor': 'Bug', 'megahorn': 'Bug', 'signal beam': 'Bug',
  'bug buzz': 'Bug', 'silver wind': 'Bug', 'fell stinger': 'Bug',
  'lunge': 'Bug', 'leech life': 'Bug', 'tera blast bug': 'Bug',
  
  // ===== ROCK =====
  'rock throw': 'Rock', 'smack down': 'Rock', 'rollout': 'Rock',
  'rock slide': 'Rock', 'stone edge': 'Rock', 'ancient power': 'Rock',
  'rock blast': 'Rock', 'power gem': 'Rock', 'rock tomb': 'Rock',
  'rock wrecker': 'Rock', 'meteor beam': 'Rock',
  'tera blast rock': 'Rock', 'head smash': 'Rock',
  
  // ===== GHOST =====
  'shadow claw': 'Ghost', 'hex': 'Ghost', 'lick': 'Ghost', 'astonish': 'Ghost',
  'shadow ball': 'Ghost', 'shadow punch': 'Ghost', 'ominous wind': 'Ghost',
  'night shade': 'Ghost', 'shadow sneak': 'Ghost',
  'shadow force': 'Ghost', 'phantom force': 'Ghost', 'poltergeist': 'Ghost',
  'tera blast ghost': 'Ghost', 'shadow bone': 'Ghost',
  
  // ===== DRAGON =====
  'dragon breath': 'Dragon', 'dragon tail': 'Dragon',
  'dragon claw': 'Dragon', 'dragon pulse': 'Dragon', 'draco meteor': 'Dragon',
  'outrage': 'Dragon', 'twister': 'Dragon', 'dragon rush': 'Dragon',
  'breaking swipe': 'Dragon', 'tera blast dragon': 'Dragon',
  'spacial rend': 'Dragon', 'roar of time': 'Dragon',
  
  // ===== DARK =====
  'bite': 'Dark', 'snarl': 'Dark', 'feint attack': 'Dark', 'sucker punch': 'Dark',
  'crunch': 'Dark', 'dark pulse': 'Dark', 'foul play': 'Dark',
  'night slash': 'Dark', 'payback': 'Dark', 'brutal swing': 'Dark',
  'darkest lariat': 'Dark', 'obstruct': 'Dark',
  'tera blast dark': 'Dark',
  
  // ===== STEEL =====
  'metal claw': 'Steel', 'bullet punch': 'Steel', 'iron tail': 'Steel',
  'steel wing': 'Steel',
  'iron head': 'Steel', 'flash cannon': 'Steel', 'magnet bomb': 'Steel',
  'heavy slam': 'Steel', 'meteor mash': 'Steel', 'mirror shot': 'Steel',
  'doom desire': 'Steel', 'tera blast steel': 'Steel',
  'double iron bash': 'Steel',
  
  // ===== FAIRY =====
  'charm': 'Fairy', 'fairy wind': 'Fairy',
  'play rough': 'Fairy', 'dazzling gleam': 'Fairy', 'moonblast': 'Fairy',
  'draining kiss': 'Fairy', 'disarming voice': 'Fairy',
  'tera blast fairy': 'Fairy', 'nature\'s madness': 'Fairy',
  'natures madness': 'Fairy',
};

// Get move type from move name using comprehensive lookup
const getMoveTypeFromName = (moveName) => {
  if (!moveName) return 'Normal';
  const name = moveName.toLowerCase().trim();
  
  // Direct lookup first (most reliable)
  if (MOVE_TYPE_MAP[name]) return MOVE_TYPE_MAP[name];
  
  // Try without special characters
  const cleaned = name.replace(/[^a-z\s]/g, '').trim();
  if (MOVE_TYPE_MAP[cleaned]) return MOVE_TYPE_MAP[cleaned];
  
  // Try partial match for variants like "Hidden Power Fire"
  if (name.startsWith('hidden power')) {
    const hpType = name.replace('hidden power', '').trim();
    const types = ['Fire','Water','Grass','Electric','Ice','Fighting','Poison','Ground','Flying','Psychic','Bug','Rock','Ghost','Dragon','Dark','Steel','Fairy'];
    const match = types.find(t => t.toLowerCase() === hpType);
    return match || 'Normal';
  }
  if (name.startsWith('weather ball')) {
    const wbType = name.replace('weather ball', '').trim();
    const types = {'fire': 'Fire', 'water': 'Water', 'ice': 'Ice', 'rock': 'Rock'};
    return types[wbType] || 'Normal';
  }
  if (name.startsWith('techno blast')) {
    const tbType = name.replace('techno blast', '').trim();
    const types = {'burn': 'Fire', 'chill': 'Water', 'douse': 'Water', 'frost': 'Ice', 'shock': 'Electric'};
    return types[tbType] || 'Normal';
  }
  
  return 'Normal';
};

// Get type gradient (matching Pokemon detail page style)
const getTypeGradient = (type) => {
  const gradients = {
    'Normal': 'from-gray-400 to-gray-500',
    'Fire': 'from-red-400 to-orange-500',
    'Water': 'from-blue-400 to-cyan-500',
    'Electric': 'from-yellow-400 to-amber-500',
    'Grass': 'from-green-400 to-emerald-500',
    'Ice': 'from-cyan-300 to-blue-400',
    'Fighting': 'from-orange-500 to-red-600',
    'Poison': 'from-purple-400 to-violet-600',
    'Ground': 'from-amber-500 to-yellow-700',
    'Flying': 'from-indigo-300 to-blue-500',
    'Psychic': 'from-pink-400 to-purple-500',
    'Bug': 'from-lime-400 to-green-600',
    'Rock': 'from-yellow-600 to-amber-700',
    'Ghost': 'from-purple-600 to-indigo-800',
    'Dragon': 'from-indigo-500 to-purple-700',
    'Dark': 'from-gray-600 to-gray-800',
    'Steel': 'from-gray-400 to-slate-600',
    'Fairy': 'from-pink-300 to-rose-500'
  };
  return gradients[type] || 'from-gray-400 to-gray-500';
};

const MovesetPanel = ({
  fastMove,
  chargedMoves = [],
  fastMoveType,
  chargedMoveTypes = [],
  variant = 'default', // 'default' | 'compact' | 'detailed'
  language: languageProp,
  className = ''
}) => {
  // Use language context (with prop fallback for backward compatibility)
  const { language: contextLanguage, t: contextT } = useLanguage();
  const language = languageProp || contextLanguage;
  const t = contextT;
  
  // Re-render when game i18n data finishes loading (for move name translation)
  const i18nReady = useGameI18nReady();

  // Ensure chargedMoves is an array
  const chargedMovesArray = Array.isArray(chargedMoves) 
    ? chargedMoves 
    : chargedMoves 
      ? [chargedMoves] 
      : [];

  // Get move types (use provided or infer from ENGLISH names - always use English for type detection)
  const fastType = fastMoveType || getMoveTypeFromName(fastMove);
  const chargedTypes = chargedMoveTypes.length > 0 
    ? chargedMoveTypes 
    : chargedMovesArray.map(move => getMoveTypeFromName(move));

  const isCompact = variant === 'compact';
  
  // Translate move names to current language
  const displayFastMove = fastMove ? translateMoveName(fastMove, language) : null;
  const displayChargedMoves = chargedMovesArray.map(move => translateMoveName(move, language));

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Fast Move */}
      <div>
        <div className={`flex items-center space-x-2 ${isCompact ? 'mb-1' : 'mb-1.5'}`}>
          <span className="text-base">⚡</span>
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {t('moves.fast')}
          </span>
        </div>
        <div className={`rounded-lg px-3 py-2 bg-gradient-to-r ${getTypeGradient(fastType)} shadow-sm flex items-center gap-2`}>
          <img 
            src={`/images/Types/${fastType.charAt(0).toUpperCase() + fastType.toLowerCase().slice(1)}.webp`}
            alt={fastType}
            className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} object-contain flex-shrink-0`}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <span className={`${isCompact ? 'text-xs' : 'text-sm'} font-semibold text-white drop-shadow-sm`}>
            {displayFastMove || '-'}
          </span>
        </div>
      </div>
      
      {/* Charged Moves */}
      <div>
        <div className={`flex items-center space-x-2 ${isCompact ? 'mb-1' : 'mb-1.5'}`}>
          <span className="text-base">💥</span>
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {t('moves.charged')}
          </span>
        </div>
        <div className={`flex ${isCompact ? 'gap-1.5' : 'gap-2'} flex-wrap`}>
          {displayChargedMoves.length > 0 ? (
            displayChargedMoves.map((move, index) => {
              const moveType = chargedTypes[index] || getMoveTypeFromName(chargedMovesArray[index]);
              return (
                <div 
                  key={index}
                  className={`rounded-lg px-3 py-2 bg-gradient-to-r ${getTypeGradient(moveType)} shadow-sm flex items-center gap-2 ${isCompact ? 'flex-1 min-w-[100px]' : 'flex-1 min-w-[120px]'}`}
                >
                  <img 
                    src={`/images/Types/${moveType.charAt(0).toUpperCase() + moveType.toLowerCase().slice(1)}.webp`}
                    alt={moveType}
                    className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} object-contain flex-shrink-0`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <span className={`${isCompact ? 'text-xs' : 'text-sm'} font-semibold text-white drop-shadow-sm`}>
                    {move}
                  </span>
                </div>
              );
            })
          ) : (
            <div className={`rounded-lg px-3 py-2 bg-gradient-to-r ${getTypeGradient('Normal')} shadow-sm flex items-center gap-2 ${isCompact ? 'flex-1 min-w-[100px]' : 'flex-1 min-w-[120px]'}`}>
              <img 
                src="/images/Types/Normal.webp"
                alt="Normal"
                className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} object-contain flex-shrink-0`}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span className={`${isCompact ? 'text-xs' : 'text-sm'} font-semibold text-white drop-shadow-sm`}>
                -
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MovesetPanel;
